import React, { useEffect, useState } from 'react';
import { Card, Descriptions, Tag, Button, Space, message, Spin, Divider, Modal, App, Table, Collapse } from 'antd';
import { ArrowLeftOutlined, EditOutlined, CopyOutlined, DownloadOutlined, DeleteOutlined, CheckOutlined, DollarOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../components/Layout/MainLayout';
import SmartEditor from '../../components/Common/SmartEditor';
import { draftService } from '../../services/draftService';
import { DraftDetailResponse } from '../../types/draft';
import { useAuthStore } from '../../store/authStore';
import dayjs from 'dayjs';

const DraftView: React.FC = () => {
  const { idx } = useParams<{ idx: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { modal } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DraftDetailResponse | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editReason, setEditReason] = useState('');
  const [historyViewType, setHistoryViewType] = useState<'table' | 'timeline' | 'collapse'>('collapse');

  // 권한 확인 (Confirm_Grade가 2인 경우 승인/비용처리 버튼 표시)
  const hasConfirmAuth = user?.Confirm_Grade === 2;

  useEffect(() => {
    if (idx) {
      fetchDraftDetail(parseInt(idx));
    }
  }, [idx]);

  const fetchDraftDetail = async (draftIdx: number) => {
    setLoading(true);
    try {
      const response = await draftService.getDraftDetail(draftIdx);
      console.log('📥 기안서 상세 응답:', response);
      if (response.resultCode === 200) {
        console.log('📊 기안서 데이터:', response.data);
        console.log('📎 첨부파일:', response.data.info.File_Names);
        console.log('📝 수정 이력:', response.data.history);
        setData(response.data);
      } else {
        message.error(response.message || '기안서 조회 실패');
      }
    } catch (error) {
      console.error('기안서 상세 조회 오류:', error);
      message.error('기안서를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFile = (fileName: string) => {
    window.open(`/download/${fileName}`, '_blank');
  };

  // 전체 파일 다운로드 (ZIP)
  const handleDownloadAll = async () => {
    if (!info || !fileReNames || fileReNames.length === 0) {
      message.warning('다운로드할 파일이 없습니다.');
      return;
    }

    try {
      const response = await fetch('/download-all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ files: fileReNames })
      });

      const result = await response.json();
      if (result.success) {
        window.open('/download-all', '_blank');
        message.success('전체 파일 다운로드를 시작합니다.');
      } else {
        message.error('다운로드 준비 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('전체 다운로드 오류:', error);
      message.error('다운로드 중 오류가 발생했습니다.');
    }
  };

  const handleDelete = () => {
    if (!info) {
      message.error('기안서 정보를 찾을 수 없습니다.');
      return;
    }

    modal.confirm({
      title: '기안서 삭제',
      content: '정말 이 기안서를 삭제하시겠습니까?',
      okText: '삭제',
      okType: 'danger',
      cancelText: '취소',
      onOk: async () => {
        try {
          const result = await draftService.deleteDraft(info.Draft_IDX);

          if (result.resultCode === 200) {
            message.success('기안서가 삭제되었습니다.');
            navigate('/draft/list');
          } else {
            message.error(result.message || '삭제 실패');
          }
        } catch (error) {
          console.error('삭제 오류:', error);
          message.error('삭제 중 오류가 발생했습니다.');
        }
      },
    });
  };

  const handleEditClick = () => {
    setEditReason('');
    setEditModalVisible(true);
  };

  const handleEditConfirm = () => {
    if (!editReason.trim()) {
      message.warning('수정 사유를 입력해주세요.');
      return;
    }

    // 수정 페이지로 이동하면서 수정 사유 전달
    navigate(`/draft/modify/${info?.Draft_IDX}`, {
      state: { editReason }
    });
  };

  // 승인 처리
  const handleConfirm = () => {
    if (!info) return;

    modal.confirm({
      title: '기안서 승인',
      content: '해당 기안서를 승인하시겠습니까?',
      okText: '승인',
      cancelText: '취소',
      onOk: async () => {
        try {
          const result = await draftService.confirmDraft([info.Draft_IDX]);
          if (result.resultCode === 200) {
            message.success('승인되었습니다.');
            fetchDraftDetail(info.Draft_IDX); // 새로고침
          } else {
            message.error(result.message || '승인 실패');
          }
        } catch (error) {
          console.error('승인 오류:', error);
          message.error('승인 중 오류가 발생했습니다.');
        }
      },
    });
  };

  // 비용처리
  const handlePayment = () => {
    if (!info) return;

    // VB 로직: 승인되지 않은 경우 추가 확인
    const isNotConfirmed = info.Confirm_YN !== 'Y' && info.Confirm_YN !== 1;

    modal.confirm({
      title: '비용처리 확인',
      content: isNotConfirmed
        ? '승인되지 않은 기안서입니다.\n\n그래도 비용처리 상태를 \'Y\'로 변경하시겠습니까?'
        : '비용처리 상태를 \'Y\'로 변경하시겠습니까?',
      okText: '처리',
      okType: isNotConfirmed ? 'danger' : 'primary',
      cancelText: '취소',
      onOk: async () => {
        await processPay();
      },
    });
  };

  const processPay = async () => {
    if (!info) return;
    try {
      const confirmYN = typeof info.Confirm_YN === 'string' ? info.Confirm_YN : (info.Confirm_YN === 1 ? 'Y' : 'N');
      const result = await draftService.paymentDraft([info.Draft_IDX], confirmYN);
      if (result.resultCode === 200) {
        message.success('비용처리 상태가 \'Y\'로 변경되었습니다.');
        fetchDraftDetail(info.Draft_IDX); // 새로고침
      } else {
        message.error(result.message || '비용처리 실패');
      }
    } catch (error) {
      console.error('비용처리 오류:', error);
      message.error('비용처리 중 오류가 발생했습니다.');
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div style={{ textAlign: 'center', padding: '100px' }}>
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  if (!data) {
    return (
      <MainLayout>
        <Card>
          <p>기안서 정보를 찾을 수 없습니다.</p>
        </Card>
      </MainLayout>
    );
  }

  const { info, history } = data;

  // 첨부파일 파싱 (null 체크 강화)
  const files = info.File_Names && info.File_Names.trim() !== ''
    ? info.File_Names.split(', ').filter(f => f.trim() !== '')
    : [];
  const fileReNames = info.File_ReNames && info.File_ReNames.trim() !== ''
    ? info.File_ReNames.split(', ').filter(f => f.trim() !== '')
    : [];

  console.log('🔍 파싱된 첨부파일:', files);
  console.log('🔍 파일 ReNames:', fileReNames);

  // HTML 엔티티 디코딩
  const decodeHTML = (html: string) => {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  };

  return (
    <MainLayout>
      <Card
        title={`기안서 상세보기 - ${info.Draft_Year}-${info.Draft_No}`}
        extra={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/draft/list')}>
              목록으로
            </Button>
            {hasConfirmAuth && (
              <>
                <Button
                  icon={<CheckOutlined />}
                  type="primary"
                  onClick={handleConfirm}
                  disabled={info.Confirm_YN === 'Y' || info.Confirm_YN === 1}
                  style={{ backgroundColor: info.Confirm_YN === 'Y' || info.Confirm_YN === 1 ? undefined : '#52c41a' }}
                >
                  승인
                </Button>
                <Button
                  icon={<DollarOutlined />}
                  onClick={handlePayment}
                  disabled={info.Pay_YN === 'Y' || info.Pay_YN === 1}
                  style={{
                    backgroundColor: info.Pay_YN === 'Y' || info.Pay_YN === 1 ? undefined : '#1890ff',
                    color: info.Pay_YN === 'Y' || info.Pay_YN === 1 ? undefined : '#fff'
                  }}
                >
                  비용처리
                </Button>
              </>
            )}
            <Button icon={<CopyOutlined />} onClick={() => navigate(`/draft/copy/${info.Draft_IDX}`)}>
              복사
            </Button>
            <Button icon={<EditOutlined />} type="primary" onClick={handleEditClick}>
              수정
            </Button>
            <Button icon={<DeleteOutlined />} danger onClick={handleDelete}>
              삭제
            </Button>
          </Space>
        }
      >
        {/* 수정 사유 입력 모달 */}
        <Modal
          title="수정 사유 입력"
          open={editModalVisible}
          onOk={handleEditConfirm}
          onCancel={() => setEditModalVisible(false)}
          okText="수정하기"
          cancelText="취소"
        >
          <div style={{ marginBottom: 8 }}>
            수정 사유를 입력해주세요. (히스토리에 기록됩니다)
          </div>
          <textarea
            value={editReason}
            onChange={(e) => setEditReason(e.target.value)}
            placeholder="수정 사유를 입력하세요"
            rows={4}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
        </Modal>
        <Descriptions bordered column={2}>
          <Descriptions.Item label="기안번호">
            {info.Draft_Year}-{info.Draft_No}
          </Descriptions.Item>
          <Descriptions.Item label="작성자">{info.Admin_Name}</Descriptions.Item>
          <Descriptions.Item label="제목" span={2}>{info.Draft_Subject}</Descriptions.Item>
          <Descriptions.Item label="거래처">{info.Draft_Client}</Descriptions.Item>
          <Descriptions.Item label="금액">
            {info.Draft_Cost ? info.Draft_Cost.toLocaleString() + '원' : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="등록일">{info.Reg_Date}</Descriptions.Item>
          <Descriptions.Item label="수신자">{info.Receiver || '-'}</Descriptions.Item>
          <Descriptions.Item label="승인 상태">
            <Tag color={info.Confirm_YN === 'Y' || info.Confirm_YN === 1 ? 'success' : 'default'}>
              {info.Confirm_YN === 'Y' || info.Confirm_YN === 1 ? '승인' : '대기'}
            </Tag>
            {(info.Confirm_YN === 'Y' || info.Confirm_YN === 1) && info.Confirm_Date && (
              <span style={{ marginLeft: 8, fontSize: '12px', color: '#999' }}>
                ({dayjs(info.Confirm_Date).format('YYYY-MM-DD')})
              </span>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="결재 상태">
            <Tag color={info.Pay_YN === 'Y' || info.Pay_YN === 1 ? 'success' : 'default'}>
              {info.Pay_YN === 'Y' || info.Pay_YN === 1 ? '완료' : '대기'}
            </Tag>
            {(info.Pay_YN === 'Y' || info.Pay_YN === 1) && info.Pay_Date && (
              <span style={{ marginLeft: 8, fontSize: '12px', color: '#999' }}>
                ({dayjs(info.Pay_Date).format('YYYY-MM-DD')})
              </span>
            )}
          </Descriptions.Item>
        </Descriptions>

        {/* 수정 이력 */}
        {history && history.length > 0 && (
          <>
            <Divider orientation="left" orientationMargin={0}>
              <Space>
                <strong>수정 이력</strong>
                <Button.Group size="small">
                  <Button
                    type={historyViewType === 'table' ? 'primary' : 'default'}
                    onClick={() => setHistoryViewType('table')}
                  >
                    테이블
                  </Button>
                  <Button
                    type={historyViewType === 'collapse' ? 'primary' : 'default'}
                    onClick={() => setHistoryViewType('collapse')}
                  >
                    접기
                  </Button>
                </Button.Group>
              </Space>
            </Divider>

            {/* 테이블 뷰 */}
            {historyViewType === 'table' && (
              <Table
              dataSource={history}
              rowKey="Draft_Backup_IDX"
              pagination={false}
              size="small"
              bordered
              style={{
                marginBottom: 24,
                fontSize: '13px'
              }}
              rowClassName={(record, index) =>
                index % 2 === 0 ? 'table-row-even' : 'table-row-odd'
              }
              columns={[
                {
                  title: 'NO',
                  key: 'no',
                  width: 60,
                  align: 'center' as const,
                  render: (_: any, __: any, index: number) => index + 1,
                },
                {
                  title: '제목',
                  dataIndex: 'Draft_Subject',
                  key: 'Draft_Subject',
                  render: (text: string) => text || '-',
                },
                {
                  title: '비용',
                  dataIndex: 'Draft_Cost',
                  key: 'Draft_Cost',
                  width: 120,
                  align: 'right' as const,
                  render: (value: number) => value ? value.toLocaleString() + '원' : '-',
                },
                {
                  title: '지급처',
                  dataIndex: 'Draft_Client',
                  key: 'Draft_Client',
                  width: 150,
                  render: (text: string) => text || '-',
                },
                {
                  title: '수정일',
                  dataIndex: 'Edit_Date',
                  key: 'Edit_Date',
                  width: 160,
                  align: 'center' as const,
                  render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-',
                },
                {
                  title: '승인',
                  dataIndex: 'Confirm_YN',
                  key: 'Confirm_YN',
                  width: 70,
                  align: 'center' as const,
                  render: (value: string) => (
                    <Tag color={value === 'Y' ? 'success' : 'default'}>
                      {value === 'Y' ? 'Y' : 'N'}
                    </Tag>
                  ),
                },
                {
                  title: '비용처리',
                  dataIndex: 'Pay_YN',
                  key: 'Pay_YN',
                  width: 80,
                  align: 'center' as const,
                  render: (value: string) => (
                    <Tag color={value === 'Y' ? 'success' : 'default'}>
                      {value === 'Y' ? 'Y' : 'N'}
                    </Tag>
                  ),
                },
                {
                  title: '수정사유',
                  dataIndex: 'Edit_Reason',
                  key: 'Edit_Reason',
                  width: 200,
                  render: (text: string) => text || '-',
                },
              ]}
            />
            )}

            {/* Collapse 뷰 */}
            {historyViewType === 'collapse' && (
              <Collapse
                accordion
                style={{ marginTop: 16, marginBottom: 24 }}
                items={history.map((item, index) => ({
                  key: item.Draft_Backup_IDX,
                  label: (
                    <Space>
                      <Tag color="blue">{dayjs(item.Edit_Date).format('YYYY-MM-DD HH:mm')}</Tag>
                      <span style={{ fontWeight: 500 }}>{item.Draft_Subject || '제목 없음'}</span>
                      {item.Edit_Reason && <Tag color="orange">수정사유</Tag>}
                      <Tag color={item.Confirm_YN === 'Y' ? 'success' : 'default'}>
                        승인: {item.Confirm_YN === 'Y' ? 'Y' : 'N'}
                      </Tag>
                      <Tag color={item.Pay_YN === 'Y' ? 'success' : 'default'}>
                        비용처리: {item.Pay_YN === 'Y' ? 'Y' : 'N'}
                      </Tag>
                    </Space>
                  ),
                  children: (
                    <div>
                      <Descriptions size="small" column={2} bordered>
                        <Descriptions.Item label="제목">{item.Draft_Subject || '-'}</Descriptions.Item>
                        <Descriptions.Item label="비용">
                          {item.Draft_Cost ? item.Draft_Cost.toLocaleString() + '원' : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="지급처">{item.Draft_Client || '-'}</Descriptions.Item>
                        <Descriptions.Item label="수정일">
                          {dayjs(item.Edit_Date).format('YYYY-MM-DD HH:mm')}
                        </Descriptions.Item>
                        <Descriptions.Item label="승인">
                          <Tag color={item.Confirm_YN === 'Y' ? 'success' : 'default'}>
                            {item.Confirm_YN === 'Y' ? 'Y' : 'N'}
                          </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="비용처리">
                          <Tag color={item.Pay_YN === 'Y' ? 'success' : 'default'}>
                            {item.Pay_YN === 'Y' ? 'Y' : 'N'}
                          </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="수정 사유" span={2}>
                          {item.Edit_Reason ? (
                            <div style={{
                              padding: '8px',
                              background: '#fff7e6',
                              borderRadius: '4px',
                              border: '1px solid #ffd591'
                            }}>
                              {item.Edit_Reason}
                            </div>
                          ) : (
                            <span style={{ color: '#999' }}>-</span>
                          )}
                        </Descriptions.Item>
                      </Descriptions>
                    </div>
                  ),
                }))}
              />
            )}
          </>
        )}

        {/* 첨부파일 */}
        {files.length > 0 && (
          <>
            <Divider orientation="left" orientationMargin={0}>
              <Space>
                <strong>첨부파일</strong>
                <Button
                  type="primary"
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadAll}
                >
                  전체 다운로드 (ZIP)
                </Button>
              </Space>
            </Divider>
            <div style={{ marginBottom: 24 }}>
              {files.map((file, index) => (
                <Button
                  key={index}
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownloadFile(fileReNames[index] || file)}
                  style={{ marginRight: 8, marginBottom: 8 }}
                >
                  {file}
                </Button>
              ))}
            </div>
          </>
        )}

        {/* 내용 - 스마트에디터로 표시 */}
        {info.Draft_Contents && (
          <>
            <Divider orientation="left" orientationMargin={0}>
              <strong>내용</strong>
            </Divider>
            <div style={{
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              padding: '16px',
              background: '#fafafa',
              marginBottom: 24
            }}>
              <SmartEditor
                id="smarteditor-view"
                defaultValue={decodeHTML(info.Draft_Contents)}
                height="400px"
              />
            </div>
          </>
        )}

      </Card>
    </MainLayout>
  );
};

export default DraftView;
